// © Microsoft Corporation. All rights reserved.
import { IsomorphicStorage } from "./IsomorphicStorage";
import { osatCookieExistsCookieName, osatCookieName } from "./SSRInteropContract";
const isomorphicStorageKey = "__RequestDataInstance__";
/**
 * Singleton class to store client browser data sent through query param to Render Service.
 * This is only used under server environment.
 */
class RequestData {
    /**
     * Initialize the request data instance with passed in client properties
     */
    constructor(data, isPssrMode) {
        this.url = new URL(data.href);
        this.innerHeight = data.innerHeight;
        this.devicePixelRatio = data.devicePixelRatio;
        this.canUseCssGrid = data.canUseCssGrid;
        this.requestId = data.requestId;
        this.cookie = data.cookie;
        this.referer = data.referer;
        this.userAgent = data.userAgent;
        this.clientData = data.clientData;
        this.vpCetoToken = data.vpCetoToken;
        this.oneServiceHeaders = tryParseJson(data.oneServiceHeaders) || {};
        this.isPssrMode = isPssrMode;
        if (isPssrMode) {
            const osatCookieExists = getCookie(osatCookieExistsCookieName, this.cookie);
            const isOsateCookieValid = !!osatCookieExists && osatCookieExists === "1";
            const osatCookie = getCookie(osatCookieName, this.cookie);
            // When user is in interactive login scenario, OSATE is present but not valid,
            // so both conditions will fail and we will skip pure SSR
            // When OSAT is present and OSATE is valid (i.e. set to "1"), user has already signed in successfully
            // When both OSAT and OSATE are missing, user is not signed in (fallback to MUID for 1S calls)
            if ((isOsateCookieValid && osatCookie) || (!osatCookieExists && !osatCookie)) {
                this.msalAuthReady = true;
                if (osatCookie) {
                    // When user has signed in, set JWT token in 1S request header for authentication
                    this.oneServiceHeaders["Authorization"] = `Bearer ${osatCookie}`;
                }
                return;
            }
            this.msalAuthReady = false;
            if (osatCookieExists && !isOsateCookieValid) {
                this.pssrRejectedReason = "interactiveLogin";
            }
            else if (osatCookieExists && !osatCookie) {
                this.pssrRejectedReason = "missOSAT";
            }
            else {
                this.pssrRejectedReason = "missOSATE";
            }
        }
    }
    /**
     * Returns singleton class instance.
     * @returns RequestData
     */
    static getInstance() {
        const instance = IsomorphicStorage.get(isomorphicStorageKey);
        if (instance) {
            return instance;
        }
        if (global["TEST_ENV"]) {
            return resetRequestData({
                href: "http://localhost:8080/",
                innerHeight: 768,
                devicePixelRatio: 1,
                canUseCssGrid: false,
                requestId: "0",
                cookie: "",
                userAgent: "",
                referer: "",
                oneServiceHeaders: "",
                vpCetoToken: ""
            });
        }
        // Throw error if the request data has not been set as this should never be the case.
        // This likely highlights that the request data was not initialized when handling a
        // request when server side rendering.
        // TODO - http://osgvsowi/24832691 renable throwing an error.
        // This is currently disabled as other singletons that are initialized at import time use
        // this they will throw an error before anyone has the chance to initialize the request data
        return resetRequestData({
            href: "http://localhost:8080/",
            innerHeight: 0,
            devicePixelRatio: 0,
            canUseCssGrid: false,
            requestId: "0",
            cookie: "",
            userAgent: "",
            referer: "",
            oneServiceHeaders: "",
            vpCetoToken: ""
        });
    }
    /**
     * Reset singleton class instance with passed in info and client settings
     * @returns RequestData
     */
    static resetInstance(data, isPssr = false) {
        const newInstance = new RequestData(data, isPssr);
        IsomorphicStorage.set(isomorphicStorageKey, newInstance);
        return newInstance;
    }
}
/**
 * Helper to get cookie content from cookie string
 *
 * @param name cookie name
 * @param cookieString cookie string with which to search for the content by `name`
 * @returns
 */
function getCookie(name, cookieString) {
    if (cookieString && name) {
        const re = new RegExp("\\b" + name + "\\s*=\\s*([^;]*)", "i");
        const match = re.exec(cookieString);
        return (match && match.length > 1 ? match[1] : null);
    }
    return null;
}
/**
 * Helper to safely parse JSON
 * @param json The JSON string to parse
 * @returns The parsed JSON object, or undefined if invalid.
 */
function tryParseJson(json) {
    try {
        if (json) {
            return JSON.parse(json);
        }
    }
    catch (ex) {
        // tslint:disable-next-line:no-empty
    }
}
/**
 * Export function to reset the request data singleton.
 * This must be used to set the request data.
 */
export const resetRequestData = RequestData.resetInstance;
/**
 * Export get instance function for testing
 */
export const getRequestData = () => (RequestData.getInstance());
/**
 * Export utility functions for unit testing purposes only
 */
export const __test__ = {
    getCookie,
    tryParseJson
};
//# sourceMappingURL=requestData.js.map